import { Reference } from "@apollo/client";

import useMutationWithValidation from "@hooks/useMutationWithValidation";
import {
  GetJourneyWithPlanDocument,
  GetJourneyWithPlanQuery,
  GetJourneyWithPlanQueryVariables,
  GoalCreateDocument,
  GoalCreateMutation,
  GoalCreateMutationVariables,
  WorksheetFragmentDoc
} from "@typing/Generated";

type Args = {
  journey: GetJourneyWithPlanQuery["journey"] | null;
};

const useCreateGoal = ({ journey }: Args) => {
  const [createGoalMutation] = useMutationWithValidation<GoalCreateMutation, GoalCreateMutationVariables>(
    GoalCreateDocument,
    "goal",
    {
      update: (store, { data }) => {
        if (!journey || !data?.goalCreate?.goal) return;

        // Add new worksheets to the journey
        data.goalCreate.goal.worksheets
          .filter(worksheet => !journey?.worksheets.some(cl => worksheet.id === cl.id))
          .map(worksheet => {
            store.modify({
              fields: {
                worksheets(cachedWorksheetRefs: readonly Reference[]) {
                  const newWorksheetRef = store.writeFragment({
                    data: worksheet,
                    fragment: WorksheetFragmentDoc,
                    fragmentName: "Worksheet"
                  });
                  if (newWorksheetRef) {
                    return [...cachedWorksheetRefs, newWorksheetRef];
                  }
                  return [...cachedWorksheetRefs];
                }
              },
              id: store.identify({ ...journey })
            });
          });

        // Now, add the goal to the journey care plan query
        try {
          const cacheData = store.readQuery<GetJourneyWithPlanQuery, GetJourneyWithPlanQueryVariables>({
            query: GetJourneyWithPlanDocument,
            variables: { id: journey.id }
          });
          if (cacheData) {
            const goals = cacheData.journey.goals ?? [];
            const newCacheData = {
              ...cacheData,
              journey: {
                ...cacheData.journey,
                goals: [...goals, data.goalCreate.goal]
              }
            };
            store.writeQuery<GetJourneyWithPlanQuery, GetJourneyWithPlanQueryVariables>({
              data: newCacheData,
              query: GetJourneyWithPlanDocument
            });
          }
        } catch {
          // The query will raise an error if we haven't asked for the journey's goals
        }

        journey.goals.map(goal => {
          store.modify({
            fields: {
              position() {
                return goal.position + 1;
              }
            },
            id: store.identify({ ...goal })
          });
        });
      }
    }
  );
  return createGoalMutation;
};

export default useCreateGoal;
